import React, { useEffect, useState, useMemo } from "react";
import { Link } from "react-router-dom";
import { api, formatCurrency, formatDate, formatApiErrorDetail } from "../lib/api";
import { Repeat, CheckCircle, XCircle, ArrowSquareOut, WarningCircle } from "@phosphor-icons/react";
import { toast } from "sonner";

const STATUS_META = {
  active:    { label: "Attivo",        cls: "bg-[#008A3C] text-white border-[#008A3C]" },
  trialing:  { label: "In prova",      cls: "bg-blue-100 text-blue-800 border-blue-300" },
  past_due:  { label: "In ritardo",    cls: "bg-yellow-100 text-yellow-900 border-yellow-300" },
  unpaid:    { label: "Non pagato",    cls: "bg-red-100 text-red-800 border-red-300" },
  canceled:  { label: "Cancellato",    cls: "bg-gray-200 text-gray-700 border-gray-300" },
  completed: { label: "Completato",    cls: "bg-[#0033A0] text-white border-[#0033A0]" },
  incomplete:{ label: "Incompleto",    cls: "bg-orange-100 text-orange-800 border-orange-300" },
};

export default function Abbonamenti() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all"); // all | active | canceled

  async function load() {
    setLoading(true);
    try {
      const { data } = await api.get("/subscriptions");
      setItems(data || []);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Errore caricamento");
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => { load(); }, []);

  async function cancelSub(sub) {
    const ok = window.confirm(
      `Vuoi davvero cancellare l'abbonamento di ${sub.cliente_nome || "questo cliente"}?\n\nNon verranno più addebitate rate. Le rate già incassate restano.`
    );
    if (!ok) return;
    try {
      await api.post(`/subscriptions/${sub.stripe_subscription_id}/cancel`);
      toast.success("Abbonamento cancellato");
      await load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Errore cancellazione");
    }
  }

  const filtered = useMemo(() => {
    if (filter === "active") return items.filter(s => ["active", "trialing", "past_due"].includes(s.status));
    if (filter === "canceled") return items.filter(s => ["canceled", "completed"].includes(s.status));
    return items;
  }, [items, filter]);

  const stats = useMemo(() => {
    const active = items.filter(s => s.status === "active").length;
    const mrr = items
      .filter(s => s.status === "active")
      .reduce((sum, s) => sum + Number(s.amount_per_rate || 0), 0);
    const incassato_totale = items.reduce((sum, s) => sum + Number(s.amount_per_rate || 0) * Number(s.rate_paid || 0), 0);
    return { active, mrr, incassato_totale };
  }, [items]);

  return (
    <div className="p-4 md:p-12 max-w-[1400px] mx-auto">
      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 mb-8 md:mb-10">
        <div>
          <div className="text-[10px] uppercase tracking-[0.3em] text-[#64748B] mb-3">Ricavi ricorrenti</div>
          <h1 className="font-display text-3xl sm:text-4xl md:text-5xl font-black tracking-tighter leading-none">Abbonamenti</h1>
          <p className="text-sm text-[#64748B] mt-3">{items.length} totali · clienti SIS-DIG e altri servizi rateali</p>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 sm:gap-6 mb-8">
        <Metric label="Attivi" value={stats.active} accent="#008A3C" />
        <Metric label="MRR (ricavo mensile)" value={formatCurrency(stats.mrr)} accent="#0033A0" highlight />
        <Metric label="Incassato totale" value={formatCurrency(stats.incassato_totale)} />
      </div>

      {/* Filter tabs */}
      <div className="flex gap-1 mb-4 border-b border-[#E2E8F0]">
        {[
          { id: "all", label: "Tutti" },
          { id: "active", label: "Attivi" },
          { id: "canceled", label: "Cancellati / Completati" },
        ].map(t => (
          <button
            key={t.id}
            onClick={() => setFilter(t.id)}
            data-testid={`sub-filter-${t.id}`}
            className={`px-4 py-2.5 text-[10px] uppercase tracking-[0.15em] font-bold border-b-2 -mb-px transition-all ${
              filter === t.id ? "border-[#0033A0] text-[#0033A0]" : "border-transparent text-[#64748B] hover:text-[#0A0A0A]"
            }`}
          >
            {t.label}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="text-center py-12 text-[#64748B] text-sm">Caricamento…</div>
      ) : filtered.length === 0 ? (
        <div className="swiss-card p-12 text-center">
          <Repeat size={40} weight="duotone" className="mx-auto text-[#64748B] mb-3" />
          <h3 className="font-display text-xl font-bold mb-2">Nessun abbonamento</h3>
          <p className="text-sm text-[#64748B]">
            Gli abbonamenti compaiono qui quando un cliente attiva un pagamento rateale su un preventivo.
            Per abilitarlo su un preventivo, apri il preventivo e attiva "Pagamento rateale mensile".
          </p>
        </div>
      ) : (
        <div className="swiss-card overflow-hidden">
          {/* Tabella desktop */}
          <div className="hidden md:block overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-[#F4F5F7]">
                <tr className="text-left">
                  <th className="py-3 px-4 text-[10px] uppercase tracking-[0.15em] text-[#64748B] font-bold">Cliente</th>
                  <th className="py-3 px-4 text-[10px] uppercase tracking-[0.15em] text-[#64748B] font-bold">Preventivo</th>
                  <th className="py-3 px-4 text-[10px] uppercase tracking-[0.15em] text-[#64748B] font-bold">Rate</th>
                  <th className="py-3 px-4 text-[10px] uppercase tracking-[0.15em] text-[#64748B] font-bold text-right">Rata</th>
                  <th className="py-3 px-4 text-[10px] uppercase tracking-[0.15em] text-[#64748B] font-bold text-right">Incassato</th>
                  <th className="py-3 px-4 text-[10px] uppercase tracking-[0.15em] text-[#64748B] font-bold">Stato</th>
                  <th className="py-3 px-4 text-[10px] uppercase tracking-[0.15em] text-[#64748B] font-bold">Ultimo</th>
                  <th className="py-3 px-4"></th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((s) => {
                  const meta = STATUS_META[s.status] || STATUS_META.active;
                  const rate_paid = Number(s.rate_paid || 0);
                  const rate_total = Number(s.rate_count || 12);
                  const rata = Number(s.amount_per_rate || 0);
                  const pct = Math.min(100, Math.round((rate_paid / rate_total) * 100));
                  return (
                    <tr key={s.stripe_subscription_id} className="border-t border-[#E2E8F0] hover:bg-[#F4F5F7]" data-testid={`sub-row-${s.stripe_subscription_id}`}>
                      <td className="py-3 px-4 font-semibold">{s.cliente_nome || "—"}</td>
                      <td className="py-3 px-4">
                        {s.quote_id ? (
                          <Link to={`/preventivi/${s.quote_id}`} className="text-[#0033A0] hover:underline font-mono text-xs flex items-center gap-1">
                            {s.quote_numero} <ArrowSquareOut size={12} />
                          </Link>
                        ) : (
                          <span className="font-mono text-xs">{s.quote_numero || "—"}</span>
                        )}
                      </td>
                      <td className="py-3 px-4">
                        <div className="flex items-center gap-2">
                          <span className="font-mono text-xs">{rate_paid}/{rate_total}</span>
                          <div className="w-16 h-1.5 bg-gray-200 rounded-full overflow-hidden">
                            <div className="h-full bg-[#008A3C]" style={{ width: `${pct}%` }} />
                          </div>
                        </div>
                      </td>
                      <td className="py-3 px-4 text-right font-mono">{formatCurrency(rata)}</td>
                      <td className="py-3 px-4 text-right font-mono font-bold">{formatCurrency(rata * rate_paid)}</td>
                      <td className="py-3 px-4">
                        <span className={`inline-block px-2 py-0.5 text-[9px] uppercase tracking-[0.15em] font-bold border rounded-sm ${meta.cls}`}>{meta.label}</span>
                      </td>
                      <td className="py-3 px-4 text-[#64748B] text-xs">{s.last_paid_at ? formatDate(s.last_paid_at) : "—"}</td>
                      <td className="py-3 px-4 text-right">
                        {["active", "trialing", "past_due"].includes(s.status) && (
                          <button
                            onClick={() => cancelSub(s)}
                            className="text-[10px] uppercase tracking-[0.15em] font-bold text-[#D32F2F] px-2 py-1 hover:bg-red-50 rounded-sm"
                            data-testid={`cancel-sub-${s.stripe_subscription_id}`}
                          >
                            Cancella
                          </button>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {/* Card mobile */}
          <div className="md:hidden divide-y divide-[#E2E8F0]">
            {filtered.map((s) => {
              const meta = STATUS_META[s.status] || STATUS_META.active;
              const rate_paid = Number(s.rate_paid || 0);
              const rate_total = Number(s.rate_count || 12);
              const rata = Number(s.amount_per_rate || 0);
              const pct = Math.min(100, Math.round((rate_paid / rate_total) * 100));
              return (
                <div key={s.stripe_subscription_id} className="px-4 py-4" data-testid={`sub-card-${s.stripe_subscription_id}`}>
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2 flex-wrap mb-1">
                        <span className={`inline-block px-2 py-0.5 text-[9px] uppercase tracking-[0.15em] font-bold border rounded-sm ${meta.cls}`}>{meta.label}</span>
                        <span className="font-mono text-[10px] text-[#64748B]">{rate_paid}/{rate_total}</span>
                      </div>
                      <div className="font-semibold text-sm truncate">{s.cliente_nome || "—"}</div>
                      <div className="text-[11px] text-[#64748B] mt-0.5">
                        {s.quote_numero} · {formatCurrency(rata)}/mese
                      </div>
                    </div>
                    <div className="font-mono font-bold text-sm flex-shrink-0">{formatCurrency(rata * rate_paid)}</div>
                  </div>
                  <div className="w-full h-1 bg-gray-200 rounded-full overflow-hidden mt-2">
                    <div className="h-full bg-[#008A3C]" style={{ width: `${pct}%` }} />
                  </div>
                  {["active", "trialing", "past_due"].includes(s.status) && (
                    <div className="flex justify-end mt-2">
                      <button onClick={() => cancelSub(s)} className="text-[10px] uppercase tracking-[0.15em] font-bold text-[#D32F2F] px-2 py-1 -mr-2">
                        Cancella
                      </button>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}

function Metric({ label, value, accent, highlight }) {
  return (
    <div
      className={`swiss-card p-3 sm:p-6 ${highlight ? "bg-[#0A0A0A] text-white border-[#0A0A0A]" : ""}`}
      style={accent && !highlight ? { borderLeft: `4px solid ${accent}` } : {}}
    >
      <div className={`text-[10px] uppercase tracking-[0.2em] ${highlight ? "text-white/60" : "text-[#64748B]"}`}>{label}</div>
      <div className={`font-display text-2xl sm:text-3xl font-black tracking-tighter mt-2 sm:mt-3 ${highlight ? "text-white" : ""}`} style={accent && !highlight ? { color: accent } : {}}>
        {value}
      </div>
    </div>
  );
}
